import type {
  HomeCapexBlock,
  HomeCustosSubsistemaBlock,
  HomeEscopoBlock,
  HomeOutlookBlock,
  HomeOverview,
  HomeTipoObraBlock,
} from "@/modules/home";
import {
  formatDecimal,
  formatMilhoes,
  formatMilhoesInteiro,
  formatPercentual,
  formatQuantidade,
} from "../../home.format";
import { HomeCard } from "../home-card";
import { HomeChart } from "../home-chart";
import { VariationBadge } from "../variation-badge";
import styles from "./overview-view.module.scss";

/** Cartão "Capex dos N Anos": total do período e a curva ano a ano (AC5). */
function CapexCard({ capex }: { capex: HomeCapexBlock }) {
  return (
    <HomeCard
      title={`Capex dos ${formatQuantidade(capex.anos)} Anos`}
      aside={
        <span className={styles.capexTotal}>
          Total: <strong>{formatMilhoes(capex.total)}</strong>
        </span>
      }
      className={styles.capex}
      testId="home-card-capex"
    >
      <HomeChart
        points={capex.serie}
        ariaLabel={`Capex por ano, em milhões de reais. Total de ${formatMilhoes(capex.total)}.`}
        labelHeader="Ano"
        valueHeader="Capex"
        formatValue={formatMilhoes}
        formatPointLabel={formatDecimal}
        showPointLabels
        showAxisLabels
        baseline="relevo"
        className={styles.capexChart}
        testId="home-chart-capex"
      />
    </HomeCard>
  );
}

/** Cartão "Outlook de Capex de Obras": projeção do ano e contagem de Obras. */
function OutlookCard({ outlook }: { outlook: HomeOutlookBlock }) {
  return (
    <HomeCard
      title={`Outlook de Capex de Obras - ${outlook.ano}`}
      className={styles.outlook}
      testId="home-card-outlook"
    >
      <div className={styles.outlookHeadline}>
        <strong className={styles.outlookValue}>{formatMilhoes(outlook.valor)}</strong>
        <VariationBadge
          value={outlook.variacaoOrcamentoPercentual}
          suffix="vs orçamento"
          testId="home-outlook-variacao"
        />
      </div>

      {/* Empilhados no Desktop e lado a lado no responsivo, como no protótipo. */}
      <div className={styles.outlookTiles}>
        <div className={styles.tile}>
          <span className={styles.tileLabel}>Total de Obras</span>
          <strong className={styles.tileValue}>{formatQuantidade(outlook.totalObras)}</strong>
        </div>

        <div className={styles.tile}>
          <span className={styles.tileLabel}>Obras Executadas</span>
          <strong className={styles.tileValue}>{formatQuantidade(outlook.obrasExecutadas)}</strong>
        </div>
      </div>
    </HomeCard>
  );
}

/** Cartão "Total por Tipo de obra": um quadro por sigla, com valor e fatia. */
function TipoObraCard({ tipoObra }: { tipoObra: HomeTipoObraBlock }) {
  return (
    <HomeCard
      title={`Total por Tipo de obra - ${tipoObra.ano}`}
      className={styles.tipoObra}
      testId="home-card-tipo-obra"
    >
      <ul className={styles.tipoObraGrid}>
        {tipoObra.itens.map((item) => (
          <li key={item.sigla} className={styles.tile} data-testid={`home-tipo-obra-${item.sigla}`}>
            <span className={styles.tileLabel}>{item.sigla}</span>
            <strong className={styles.tileValue}>{formatMilhoes(item.valor)}</strong>
            <span className={styles.tileShare}>{formatPercentual(item.percentual)}</span>
          </li>
        ))}
      </ul>
    </HomeCard>
  );
}

/**
 * Serviços, Materiais e Facilidades — três dos sete blocos de indicadores da
 * Visão Geral (AC2), apresentados como faixas empilhadas no protótipo.
 */
function EscopoCards({ escopo }: { escopo: HomeEscopoBlock[] }) {
  return (
    <div className={styles.escopo}>
      {escopo.map((bloco) => (
        <div key={bloco.id} className={styles.escopoRow} data-testid={`home-card-${bloco.id}`}>
          <span className={styles.escopoLabel}>{bloco.label}</span>
          <strong className={styles.escopoPercentual}>{formatPercentual(bloco.percentual)}</strong>

          <div className={styles.escopoMeta}>
            <span className={styles.escopoValor}>{formatMilhoes(bloco.valor)}</span>
            <VariationBadge
              value={bloco.variacaoPercentual}
              testId={`home-escopo-${bloco.id}-variacao`}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Cartão "Custos por Subsistema": ranking rolável de subsistemas. */
function CustosSubsistemaCard({ custos }: { custos: HomeCustosSubsistemaBlock }) {
  return (
    <HomeCard
      title={`Custos por Subsistema - ${custos.ano}`}
      className={styles.subsistema}
      testId="home-card-custos-subsistema"
    >
      <ul className={styles.subsistemaList}>
        {custos.itens.map((item) => (
          <li key={item.codigo} className={styles.subsistemaRow}>
            <div className={styles.subsistemaIdentity}>
              <span className={styles.subsistemaNome}>{item.nome}</span>
              <span className={styles.subsistemaCodigo}>{item.codigo}</span>
            </div>
            <span className={styles.subsistemaValor}>{formatMilhoesInteiro(item.valor)}</span>
          </li>
        ))}
      </ul>
    </HomeCard>
  );
}

/** Visão Geral: os 7 blocos de indicadores consolidados do portfólio (AC2). */
export function OverviewView({ overview }: { overview: HomeOverview }) {
  return (
    <div className={styles.grid} data-testid="home-overview">
      <CapexCard capex={overview.capex} />
      <OutlookCard outlook={overview.outlook} />
      <TipoObraCard tipoObra={overview.tipoObra} />
      <EscopoCards escopo={overview.escopo} />
      <CustosSubsistemaCard custos={overview.custosPorSubsistema} />
    </div>
  );
}
